<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

// saveHandler is a prop rather than an emit so the dialog can wait for the
// round trip: a rejected write must not discard a 4 000-character skill body.
const props = defineProps({
  saveHandler: { type: Function, required: true },
});

const emit = defineEmits(['delete']);

// The live API truncates skill bodies at 4 000 characters even though the
// published schema advertises 20 000.
const BODY_LIMIT = 4000;
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const { t } = useI18n();

const skills = useMapGetter('metaAgent/getSkills');
const uiFlags = useMapGetter('metaAgent/getUIFlags');

const dialogRef = ref(null);
const editingId = ref(null);
const title = ref('');
const description = ref('');
const body = ref('');

const isTitleValid = computed(() => SLUG.test(title.value));
const canSave = computed(
  () =>
    isTitleValid.value &&
    description.value.trim().length > 0 &&
    body.value.trim().length > 0 &&
    body.value.length <= BODY_LIMIT
);

const openDialog = skill => {
  editingId.value = skill?.id || null;
  title.value = skill?.title || '';
  description.value = skill?.description || '';
  body.value = skill?.skill || '';
  dialogRef.value.open();
};

const dialogTitle = computed(() =>
  editingId.value
    ? t('META_AGENT.SKILLS.INSTRUCTIONS.EDIT_TITLE')
    : t('META_AGENT.SKILLS.INSTRUCTIONS.ADD')
);

const save = async () => {
  const saved = await props.saveHandler({
    skillId: editingId.value,
    skill: {
      title: title.value,
      description: description.value,
      skill: body.value,
    },
  });
  if (saved) dialogRef.value.close();
};

const remove = skill => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('META_AGENT.SKILLS.INSTRUCTIONS.DELETE_CONFIRM')))
    return;
  emit('delete', skill.id);
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-start justify-between gap-4">
      <span class="text-sm text-n-slate-11">
        {{ t('META_AGENT.SKILLS.INSTRUCTIONS.DESCRIPTION') }}
      </span>
      <Button
        :label="t('META_AGENT.SKILLS.INSTRUCTIONS.ADD')"
        size="sm"
        icon="i-lucide-plus"
        @click="openDialog(null)"
      />
    </div>

    <div
      v-for="skill in skills"
      :key="skill.id"
      class="flex items-start justify-between gap-4 p-4 border rounded-xl bg-n-solid-1 border-n-weak"
    >
      <div class="flex flex-col gap-1 min-w-0">
        <span class="font-mono text-sm break-words text-n-slate-12">
          {{ skill.title }}
        </span>
        <span class="text-sm break-words text-n-slate-11">
          {{ skill.description }}
        </span>
        <span class="text-xs truncate text-n-slate-10">{{ skill.skill }}</span>
      </div>
      <div class="flex items-center flex-shrink-0 gap-1">
        <Button
          :label="t('META_AGENT.SKILLS.INSTRUCTIONS.EDIT')"
          size="xs"
          variant="faded"
          color="slate"
          @click="openDialog(skill)"
        />
        <Button
          :label="t('META_AGENT.SKILLS.INSTRUCTIONS.DELETE')"
          size="xs"
          variant="ghost"
          color="ruby"
          @click="remove(skill)"
        />
      </div>
    </div>

    <span v-if="!skills.length" class="text-sm text-n-slate-10">
      {{ t('META_AGENT.SKILLS.INSTRUCTIONS.EMPTY') }}
    </span>

    <Dialog
      ref="dialogRef"
      :title="dialogTitle"
      :confirm-button-label="t('META_AGENT.SKILLS.INSTRUCTIONS.SAVE')"
      :disable-confirm-button="!canSave"
      :is-loading="uiFlags.savingSkill"
      width="xl"
      @confirm="save"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="title"
          :label="t('META_AGENT.SKILLS.INSTRUCTIONS.TITLE')"
          :placeholder="t('META_AGENT.SKILLS.INSTRUCTIONS.TITLE_PLACEHOLDER')"
          :message="t('META_AGENT.SKILLS.INSTRUCTIONS.TITLE_HINT')"
          :message-type="title && !isTitleValid ? 'error' : 'info'"
        />
        <Input
          v-model="description"
          :label="t('META_AGENT.SKILLS.INSTRUCTIONS.SKILL_DESCRIPTION')"
          :placeholder="
            t('META_AGENT.SKILLS.INSTRUCTIONS.DESCRIPTION_PLACEHOLDER')
          "
        />
        <div class="flex flex-col gap-1">
          <TextArea
            v-model="body"
            :label="t('META_AGENT.SKILLS.INSTRUCTIONS.BODY')"
            :placeholder="t('META_AGENT.SKILLS.INSTRUCTIONS.BODY_PLACEHOLDER')"
            :max-length="BODY_LIMIT"
            min-height="10rem"
            auto-height
          />
          <span
            class="text-xs ltr:text-right rtl:text-left"
            :class="
              body.length > BODY_LIMIT ? 'text-n-ruby-9' : 'text-n-slate-11'
            "
          >
            {{
              t('META_AGENT.SKILLS.INSTRUCTIONS.CHARACTER_COUNT', {
                count: body.length,
                max: BODY_LIMIT,
              })
            }}
          </span>
        </div>
      </div>
    </Dialog>
  </div>
</template>
